import { useEffect, useState, useMemo } from 'react'
import { usePendingInvitations, type PendingInvitation } from '../hooks/usePendingInvitations'
import { usePreferences } from '../hooks/usePreferences'
import { useToast } from '../hooks/useToast'

function _timeAgo(ts: number): string {
  const seconds = Math.floor((Date.now() - ts) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

export default function PendingInvitations() {
  const { invitations, withdrawn, loading, error, fetchAll, withdraw, withdrawBulk, dismissWithdrawn, clearWithdrawn } = usePendingInvitations()
  const [withdrawingId, setWithdrawingId] = useState<string | null>(null)
  const { toast, show: showToast } = useToast()
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null)
  const [confirmBulk, setConfirmBulk] = useState(false)
  const [showWithdrawn, setShowWithdrawn] = useState(false)
  const { preferences, set: setPref } = usePreferences()
  const threshold = preferences.withdrawThreshold

  useEffect(() => { fetchAll() }, [fetchAll])

  const staleInvitations = useMemo(
    () => invitations.filter((i) => i.sentDaysAgo >= threshold),
    [invitations, threshold],
  )

  const handleThresholdChange = (days: number) => {
    setPref('withdrawThreshold', days)
    setConfirmBulk(false)
  }

  const handleWithdraw = async (inv: PendingInvitation) => {
    setWithdrawingId(inv.invitationId)
    const result = await withdraw(inv)
    setWithdrawingId(null)
    showToast(result.success
      ? `Withdrew invitation to ${inv.firstName} ${inv.lastName}`
      : 'Failed to withdraw invitation')
  }

  const handleBulkWithdraw = async () => {
    setConfirmBulk(false)
    setBulkProgress({ done: 0, total: staleInvitations.length })
    const results = await withdrawBulk(staleInvitations, (done, total) =>
      setBulkProgress({ done, total })
    )
    setBulkProgress(null)
    const succeeded = results.filter((r) => r.success).length
    showToast(`Withdrew ${succeeded} of ${results.length} stale invitation${results.length !== 1 ? 's' : ''}`)
  }

  return (
    <div className="relative flex h-full flex-1 flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-zinc-200 px-6 py-4">
        <div>
          <h2 className="text-sm font-semibold text-zinc-900">Pending Invitations</h2>
          <p className="text-xs text-zinc-500">
            {invitations.length} pending
            {staleInvitations.length > 0 && (
              <span className="ml-1 text-red-500">· {staleInvitations.length} stale</span>
            )}
            {withdrawn.length > 0 && (
              <span className="ml-1 text-zinc-400">· {withdrawn.length} withdrawn</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <span>Stale after</span>
            <select
              value={threshold}
              onChange={(e) => handleThresholdChange(Number(e.target.value))}
              className="rounded border border-zinc-200 bg-white px-2 py-1 text-xs"
            >
              <option value={7}>1 week</option>
              <option value={14}>2 weeks</option>
              <option value={21}>3 weeks</option>
              <option value={30}>1 month</option>
            </select>
          </div>

          {staleInvitations.length > 0 && !bulkProgress && (
            <button
              onClick={() => setConfirmBulk(true)}
              className="rounded-lg bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-100"
            >
              Withdraw {staleInvitations.length} stale
            </button>
          )}

          {bulkProgress && (
            <span className="text-xs text-zinc-500">
              Withdrawing {bulkProgress.done}/{bulkProgress.total}...
            </span>
          )}

          <button
            onClick={() => fetchAll(true)}
            disabled={loading}
            className="rounded-lg bg-zinc-100 px-3 py-1.5 text-xs font-medium text-zinc-600 hover:bg-zinc-200"
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Confirmation dialog */}
      {confirmBulk && (
        <div className="animate-fade-in border-b border-red-200 bg-red-50 px-6 py-3">
          <p className="text-sm font-medium text-red-800">
            Withdraw {staleInvitations.length} invitation{staleInvitations.length !== 1 ? 's' : ''} older than {threshold} days? This cannot be undone.
          </p>
          <div className="mt-2 max-h-24 overflow-y-auto text-xs text-red-600">
            {staleInvitations.map((inv) => (
              <span key={inv.invitationId} className="mr-3">
                {inv.firstName} {inv.lastName}
                <span className="ml-1 opacity-60">({inv.sentTimeLabel}{!inv.exactTimestamp ? ' ~approx' : ''})</span>
              </span>
            ))}
          </div>
          <div className="mt-2 flex gap-2">
            <button
              onClick={handleBulkWithdraw}
              className="rounded bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700"
            >
              Confirm withdraw
            </button>
            <button
              onClick={() => setConfirmBulk(false)}
              className="rounded bg-zinc-200 px-3 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {loading && invitations.length === 0 ? (
          <div className="flex h-40 items-center justify-center text-sm text-zinc-400">
            Loading sent invitations...
          </div>
        ) : error ? (
          <div className="flex h-40 items-center justify-center text-sm text-red-500">
            {error}
          </div>
        ) : invitations.length === 0 ? (
          <div className="flex h-40 items-center justify-center text-sm text-zinc-400">
            No pending invitations
          </div>
        ) : (
          invitations.map((inv) => {
            const isStale = inv.sentDaysAgo >= threshold
            return (
              <div
                key={inv.invitationId}
                className={`flex items-center justify-between border-b border-zinc-100 px-6 py-3 ${isStale ? 'bg-red-50/50' : ''}`}
              >
                <div className="flex items-center gap-3">
                  {inv.photoUrl ? (
                    <img src={inv.photoUrl} alt={`${inv.firstName} ${inv.lastName}`} className="h-9 w-9 shrink-0 rounded-full object-cover" />
                  ) : (
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-zinc-200 text-xs font-medium text-zinc-600">
                      {inv.firstName[0]}{inv.lastName[0]}
                    </div>
                  )}
                  <div>
                    <a
                      href={`https://www.linkedin.com/in/${inv.vanityName}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-zinc-900 hover:underline"
                    >
                      {inv.firstName} {inv.lastName}
                    </a>
                    <div className="flex items-center gap-2 text-xs text-zinc-400">
                      <span>
                        {inv.sentTimeLabel}
                        {!inv.exactTimestamp && (
                          <span className="ml-1 opacity-50" title="Approximate - intercepted timestamps are exact">~</span>
                        )}
                      </span>
                      {isStale && (
                        <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-600">
                          Stale
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleWithdraw(inv)}
                  disabled={withdrawingId === inv.invitationId || !!bulkProgress}
                  className="rounded-lg border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-600 hover:bg-zinc-50 disabled:opacity-50"
                >
                  {withdrawingId === inv.invitationId ? 'Withdrawing...' : 'Withdraw'}
                </button>
              </div>
            )
          })
        )}
      </div>

      {/* Withdrawn history */}
      {withdrawn.length > 0 && (
        <div className="border-t border-zinc-200">
          <div
            role="button"
            tabIndex={0}
            onClick={() => setShowWithdrawn((v) => !v)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowWithdrawn((v) => !v) } }}
            className="flex w-full cursor-pointer items-center justify-between px-6 py-3 text-left hover:bg-zinc-50"
          >
            <span className="text-xs font-medium text-zinc-500">
              Recently withdrawn ({withdrawn.length})
            </span>
            <div className="flex items-center gap-2">
              {showWithdrawn && (
                <button
                  onClick={(e) => { e.stopPropagation(); clearWithdrawn() }}
                  className="text-[10px] text-zinc-400 hover:text-zinc-600"
                >
                  Clear all
                </button>
              )}
              <svg
                className={`h-3.5 w-3.5 text-zinc-400 transition-transform ${showWithdrawn ? 'rotate-180' : ''}`}
                fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
              </svg>
            </div>
          </div>

          {showWithdrawn && (
            <div className="max-h-60 overflow-y-auto">
              {withdrawn.map((w) => (
                <div
                  key={w.profileUrn}
                  className="flex items-center justify-between border-t border-zinc-100 bg-zinc-50/50 px-6 py-2.5"
                >
                  <div className="flex items-center gap-3">
                    {w.photoUrl ? (
                      <img src={w.photoUrl} alt={`${w.firstName} ${w.lastName}`} className="h-8 w-8 shrink-0 rounded-full object-cover opacity-60" />
                    ) : (
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-200 text-xs font-medium text-zinc-400">
                        {w.firstName[0]}{w.lastName[0]}
                      </div>
                    )}
                    <div>
                      <span className="text-sm text-zinc-500">{w.firstName} {w.lastName}</span>
                      <div className="text-[11px] text-zinc-400">
                        Withdrawn {_timeAgo(w.withdrawnAt)}
                        <span className="mx-1">·</span>
                        <span className="opacity-60">{w.originalSentLabel}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <a
                      href={`https://www.linkedin.com/in/${w.vanityName}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded-lg border border-zinc-200 px-3 py-1 text-xs font-medium text-zinc-600 hover:bg-white"
                    >
                      View Profile
                    </a>
                    <button
                      onClick={() => dismissWithdrawn(w.profileUrn)}
                      className="rounded p-1 text-zinc-300 hover:text-zinc-500"
                      title="Dismiss"
                    >
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {toast && (
        <div className="absolute bottom-4 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-zinc-900 px-4 py-2.5 shadow-lg">
          <span className="text-xs text-white">{toast}</span>
        </div>
      )}
    </div>
  )
}
